// WebGL2 renderer: compiles the blur + main programs, uploads the source image,
// and renders the edited result into a canvas from an EditState.

import { HSL_COLORS, type EditState } from '../editState';
import { VERT, BLUR_FRAG, MAIN_FRAG } from './glsl';

function compile(gl: WebGL2RenderingContext, type: number, src: string) {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(`Shader compile failed: ${log}`);
  }
  return sh;
}

function link(gl: WebGL2RenderingContext, vs: string, fs: string) {
  const p = gl.createProgram()!;
  gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, vs));
  gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    throw new Error(`Program link failed: ${gl.getProgramInfoLog(p)}`);
  }
  return p;
}

// hsv-ish wheel (hue 0..360, sat 0..100, lum -100..100) -> small rgb offset.
function wheelToRgb(w: { hue: number; sat: number; lum: number }): [
  number,
  number,
  number,
] {
  const h = (w.hue / 360) * 6;
  const s = w.sat / 100;
  const i = Math.floor(h) % 6;
  const f = h - Math.floor(h);
  const table: [number, number, number][] = [
    [1, f, 0],
    [1 - f, 1, 0],
    [0, 1, f],
    [0, 1 - f, 1],
    [f, 0, 1],
    [1, 0, 1 - f],
  ];
  const [r, g, b] = table[i];
  const amt = s * 0.25 + (w.lum / 100) * 0.1;
  return [(r - 0.33) * amt, (g - 0.33) * amt, (b - 0.33) * amt];
}

export class GLRenderer {
  private gl: WebGL2RenderingContext;
  private blurProg: WebGLProgram;
  private mainProg: WebGLProgram;
  private vao: WebGLVertexArrayObject;
  private srcTex: WebGLTexture;
  private texA: WebGLTexture;
  private texB: WebGLTexture;
  private fbo: WebGLFramebuffer;
  private imgW = 1;
  private imgH = 1;

  constructor(private canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2', {
      preserveDrawingBuffer: true,
      premultipliedAlpha: false,
    });
    if (!gl) throw new Error('WebGL2 is not supported in this browser.');
    this.gl = gl;

    this.blurProg = link(gl, VERT, BLUR_FRAG);
    this.mainProg = link(gl, VERT, MAIN_FRAG);

    // Fullscreen triangle-strip quad.
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW,
    );
    const loc = gl.getAttribLocation(this.mainProg, 'a_pos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);

    this.srcTex = this.makeTex();
    this.texA = this.makeTex();
    this.texB = this.makeTex();
    this.fbo = gl.createFramebuffer()!;
  }

  private makeTex(): WebGLTexture {
    const gl = this.gl;
    const t = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return t;
  }

  setImage(source: ImageBitmap | HTMLImageElement) {
    const gl = this.gl;
    this.imgW = 'width' in source ? source.width : 1;
    this.imgH = 'height' in source ? source.height : 1;

    gl.bindTexture(gl.TEXTURE_2D, this.srcTex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      source,
    );
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);

    for (const t of [this.texA, this.texB]) {
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        this.imgW,
        this.imgH,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        null,
      );
    }
  }

  get aspect(): number {
    return this.imgW / this.imgH;
  }

  private blurPass(src: WebGLTexture, dst: WebGLTexture, dir: [number, number]) {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      dst,
      0,
    );
    gl.viewport(0, 0, this.imgW, this.imgH);
    gl.useProgram(this.blurProg);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, src);
    gl.uniform1i(gl.getUniformLocation(this.blurProg, 'u_tex'), 0);
    gl.uniform2f(gl.getUniformLocation(this.blurProg, 'u_dir'), dir[0], dir[1]);
    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  render(state: EditState) {
    const gl = this.gl;

    // Size the canvas to the cropped aspect.
    const cw = Math.max(1, Math.round(this.imgW * state.crop.width));
    const ch = Math.max(1, Math.round(this.imgH * state.crop.height));
    const maxDim = 2048;
    const scale = Math.min(1, maxDim / Math.max(cw, ch));
    this.canvas.width = Math.max(1, Math.round(cw * scale));
    this.canvas.height = Math.max(1, Math.round(ch * scale));

    // Two-pass separable blur of the source.
    const texel = 3.0;
    this.blurPass(this.srcTex, this.texA, [texel / this.imgW, 0]);
    this.blurPass(this.texA, this.texB, [0, texel / this.imgH]);

    // Main pass to the canvas.
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.useProgram(this.mainProg);
    gl.bindVertexArray(this.vao);

    const u = (n: string) => gl.getUniformLocation(this.mainProg, n);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.srcTex);
    gl.uniform1i(u('u_tex'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.texB);
    gl.uniform1i(u('u_blur'), 1);

    const n = (v: number) => v / 100; // slider -> -1..1

    gl.uniform1f(u('u_temp'), n(state.temp));
    gl.uniform1f(u('u_tint'), n(state.tint));
    gl.uniform1f(u('u_exposure'), state.exposure);
    gl.uniform1f(u('u_contrast'), n(state.contrast));
    gl.uniform1f(u('u_highlights'), n(state.highlights));
    gl.uniform1f(u('u_shadows'), n(state.shadows));
    gl.uniform1f(u('u_whites'), n(state.whites));
    gl.uniform1f(u('u_blacks'), n(state.blacks));

    gl.uniform1f(u('u_vibrance'), n(state.vibrance));
    gl.uniform1f(u('u_saturation'), n(state.saturation));

    const hue: number[] = [];
    const sat: number[] = [];
    const lum: number[] = [];
    for (const c of HSL_COLORS) {
      hue.push(n(state.hsl[c].hue));
      sat.push(n(state.hsl[c].sat));
      lum.push(n(state.hsl[c].lum));
    }
    gl.uniform1fv(u('u_hslHue'), hue);
    gl.uniform1fv(u('u_hslSat'), sat);
    gl.uniform1fv(u('u_hslLum'), lum);

    gl.uniform3fv(u('u_gradeShadow'), wheelToRgb(state.colorGrading.shadows));
    gl.uniform3fv(u('u_gradeMid'), wheelToRgb(state.colorGrading.midtones));
    gl.uniform3fv(u('u_gradeHigh'), wheelToRgb(state.colorGrading.highlights));
    gl.uniform1f(u('u_gradeBalance'), n(state.colorGrading.balance));

    gl.uniform1f(u('u_texture'), n(state.texture));
    gl.uniform1f(u('u_clarity'), n(state.clarity));
    gl.uniform1f(u('u_dehaze'), n(state.dehaze));
    gl.uniform1f(u('u_sharpen'), state.sharpenAmount / 150);

    gl.uniform4f(
      u('u_crop'),
      state.crop.x,
      state.crop.y,
      state.crop.width,
      state.crop.height,
    );
    const straighten =
      ((state.crop.angle + state.rotate) * Math.PI) / 180 +
      (state.crop.rotate90 * Math.PI) / 2;
    gl.uniform1f(u('u_straighten'), straighten);
    gl.uniform2f(
      u('u_flip'),
      state.crop.flipH ? -1 : 1,
      state.crop.flipV ? -1 : 1,
    );
    gl.uniform1f(u('u_distortion'), n(state.lensDistortion) * 0.5);
    gl.uniform2f(
      u('u_perspective'),
      n(state.perspectiveHorizontal),
      n(state.perspectiveVertical),
    );
    gl.uniform1f(u('u_vignette'), n(state.vignetteAmount));
    gl.uniform1f(u('u_vignetteMid'), state.vignetteMidpoint / 100);

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  toBlob(type = 'image/jpeg', quality = 0.92): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Export failed'))),
        type,
        quality,
      );
    });
  }

  dispose() {
    const gl = this.gl;
    gl.deleteProgram(this.blurProg);
    gl.deleteProgram(this.mainProg);
    gl.deleteTexture(this.srcTex);
    gl.deleteTexture(this.texA);
    gl.deleteTexture(this.texB);
    gl.deleteFramebuffer(this.fbo);
  }
}
